import React from 'react'
import {EmojiDict} from "../../../core/gd/const/item-icon/emoji/emoji"
import {ItemIconDict} from "../../../core/gd/const/item-icon/icon/icons"
import {ColorPalette} from "../../../core/fields3/color-coding/color-coding"
import Emoji from "../../../core/gd-components/icons/common/emoji/emoji"
import {DEFAULT_AGENT_ID, DEFAULT_AGENT_COLOR, buildDefaultAgent} from "../../settings/ai/agents/list/default-agent"

type Props = {
    agentId?        : ID | null
    icon?           : string | null
    color?          : number | null
    size?           : number
    noBackground?   : boolean
}

export default function AiAgentAvatar(props: Props) {

    const size = props.size ?? 24

    let icon  : string | null = null
    let color : number | null = null

    if (props.agentId !== undefined) {
        if (props.agentId === DEFAULT_AGENT_ID) {
            const def = buildDefaultAgent()
            icon  = def.icon
            color = def.color
        } else if (props.agentId) {
            const agent = gd.session.aiAgents.get(props.agentId)
            icon  = agent?.icon ?? null
            color = agent?.color ?? null
        }
    } else {
        icon  = props.icon ?? null
        color = props.color ?? null
    }

    // Fallback to default agent's blue when color isn't set (e.g. legacy
    // agents without metadata.color, or when agentId is null/missing).
    const effectiveColor = color ?? DEFAULT_AGENT_COLOR
    const bg = gd.skin.colors.mainColor({colorNumber: effectiveColor, corePalette: ColorPalette.CORE})

    // EMOJI — no background circle, but wrap in fixed-size flex container so it
    // aligns the same way as icon avatars in surrounding rows. In the nav panel
    // (noBackground) emoji is rendered at a larger fixed size for legibility.
    if (icon && EmojiDict[icon]) {
        const emojiSize = props.noBackground ? 24 : size
        const emojiWrap: React.CSSProperties = {
            width           : size,
            height          : size,
            flexShrink      : 0,
            display         : 'inline-flex',
            alignItems      : 'center',
            justifyContent  : 'center',
        }
        return (
            <div style={emojiWrap}>
                <Emoji name={icon} size={String(emojiSize)}/>
            </div>
        )
    }

    const circleStyle: React.CSSProperties = {
        width           : size,
        height          : size,
        borderRadius    : '50%',
        backgroundColor : bg,
        flexShrink      : 0,
        display         : 'inline-flex',
        alignItems      : 'center',
        justifyContent  : 'center',
        color           : '#fff',
    }

    // URL custom icon
    if (icon && icon.startsWith('url:')) {
        const url = icon.substring(4)
        const urlStyle: React.CSSProperties = {
            width               : size * 0.7,
            height              : size * 0.7,
            backgroundImage     : `url(${url})`,
            backgroundSize      : 'contain',
            backgroundRepeat    : 'no-repeat',
            backgroundPosition  : 'center',
        }
        if (props.noBackground) return <div style={urlStyle}/>
        return (
            <div style={circleStyle}>
                <div style={urlStyle}/>
            </div>
        )
    }

    // Standard icon: try ItemIconDict first, fallback to using the icon string
    // as a CSS class directly (e.g. 'i5-default-ai' for the built-in default agent).
    const iconClass = icon
        ? (ItemIconDict[icon]?.className ?? icon)
        : null
    const innerClass = iconClass || 'i5-default-ai'
    const innerStyle: React.CSSProperties = {fontSize: props.noBackground ? 18 : 13, lineHeight: 1}

    if (props.noBackground) return <div className={innerClass} style={innerStyle}/>

    return (
        <div style={circleStyle}>
            <div className={innerClass} style={innerStyle}/>
        </div>
    )
}
